<template>
	<div class="system-settings-container">
		<!-- 页面头部 -->
		<div class="settings-header">
			<div class="header-title">
				<span class="title-icon">⚙️</span>
				<h2>系统设置</h2>
			</div>
			<p class="header-desc">管理系统的各项配置参数</p>
		</div>

		<!-- 设置项卡片列表 -->
		<div class="settings-content">
			<div v-for="(item, index) in settingList" :key="index" class="setting-card">
				<div class="card-left">
					<div class="setting-icon">
						<span>{{ getSettingIcon(item.settingsName) }}</span>
					</div>
					<div class="setting-info">
						<label class="setting-label">{{ item.settingsName }}</label>
						<span class="setting-desc">{{ getSettingDesc(item.settingsName) }}</span>
					</div>
				</div>
				<div class="card-right">
					<!-- 字符串类型输入框 -->
					<Input 
						v-if="item.valueType == 'String'" 
						v-model="item.settingsValue" 
						placeholder="请输入内容" 
						size="large"
						class="custom-input"
						:class="{ 'input-changed': isValueChanged(item) }"
						@on-change="handleValueChange(item)"
					/>
					
					<!-- 枚举类型下拉选择 -->
					<Select 
						v-else-if="item.valueType == 'Enum'" 
						v-model="item.settingsValue" 
						size="large"
						transfer="true"
						class="custom-select"
						:class="{ 'select-changed': isValueChanged(item) }"
						@on-change="handleValueChange(item)"
					>
						<Option v-for="opt in item.items" :value="opt" :key="opt">{{ opt }}</Option>
					</Select>
					
					<!-- 保存状态提示 -->
					<div v-if="savedItems[item.settingsName]" class="saved-badge">
						<span>已保存</span>
					</div>
				</div>
			</div>
			
			<!-- 空状态 -->
			<div v-if="settingList.length === 0" class="empty-state">
				<span class="empty-icon">📋</span>
				<p>暂无设置项</p>
			</div>
		</div>

		<!-- 底部操作栏 -->
		<div class="settings-footer" v-if="hasChanges">
			<div class="footer-info">
				<span class="info-badge">有未保存的更改</span>
			</div>
			<div class="footer-actions">
				<Button size="large" @click="resetChanges" class="btn-reset">
					<span>↺</span> 重置
				</Button>
				<Button type="primary" size="large" @click="saveAllChanges" class="btn-save" :loading="saving">
					<span>💾</span> {{ saving ? '保存中...' : '保存所有更改' }}
				</Button>
			</div>
		</div>
	</div>
</template>

<script>
	import request from '@/utils/request'

	export default {
		name: 'systemsettings',
		data() {
			return {
				settingList: [],        // 当前设置列表
				originalSettingList: [], // 备份原始数据用于对比
				savedItems: {},          // 记录哪些项已保存
				saving: false            // 保存加载状态
			}
		},
		computed: {
			// 是否有未保存的修改
			hasChanges() {
				if (this.settingList.length !== this.originalSettingList.length) return true
				for (let i = 0; i < this.settingList.length; i++) {
					if (this.settingList[i].settingsValue !== this.originalSettingList[i].settingsValue) {
						return true
					}
				}
				return false
			}
		},
		methods: {
			// 获取设置项图标
			getSettingIcon(name) {
				const iconMap = {
					'网站名称': '🌐',
					'网站标题': '📌',
					'网站关键词': '🔑',
					'网站描述': '📝',
					'客服电话': '📞',
					'客服邮箱': '✉️',
					'公司地址': '📍',
					'备案号': '⚖️',
					'是否开启评论': '💬',
					'默认头像': '👤'
				}
				return iconMap[name] || '⚙️'
			},
			
			// 获取设置项描述
			getSettingDesc(name) {
				const descMap = {
					'网站名称': '设置网站的显示名称',
					'网站标题': '设置浏览器标签栏标题',
					'网站关键词': 'SEO关键词，多个用逗号分隔',
					'网站描述': 'SEO网站描述信息',
					'客服电话': '客服热线电话',
					'客服邮箱': '客服联系邮箱',
					'公司地址': '公司详细地址',
					'备案号': 'ICP备案号',
					'是否开启评论': '控制全站评论功能',
					'默认头像': '新用户默认头像'
				}
				return descMap[name] || '系统配置项'
			},
			
			// 判断值是否被修改
			isValueChanged(item) {
				const original = this.originalSettingList.find(i => i.settingsName === item.settingsName)
				return original && original.settingsValue !== item.settingsValue
			},
			
			// 处理值变化
			handleValueChange(item) {
				// 清除该项的已保存标记
				if (this.savedItems[item.settingsName]) {
					this.$delete(this.savedItems, item.settingsName)
				}
			},
			
			// 重置所有修改
			resetChanges() {
				// 恢复原始数据
				this.settingList = JSON.parse(JSON.stringify(this.originalSettingList))
				this.savedItems = {}
				this.$Notice.success({
					title: '提示',
					duration: 2,
					desc: '已重置所有未保存的更改'
				})
			},
			
			// 保存所有修改
			async saveAllChanges() {
				if (this.saving) return
				
				// 获取所有被修改的设置项
				const changedSettings = this.settingList.filter(item => this.isValueChanged(item))
				
				if (changedSettings.length === 0) {
					this.$Notice.warning({
						title: '提示',
						duration: 2,
						desc: '没有需要保存的更改'
					})
					return
				}
				
				this.saving = true
				
				try {
					// 直接调用后端接口，传入修改的配置项数组
					const response = await request.post('/api/SystemSettings/EditSystemSettings', changedSettings)
					
					if (response.state === 0) {
						throw new Error(response.errorMsg || '保存失败')
					}
					
					// 保存成功后更新原始数据备份
					this.originalSettingList = JSON.parse(JSON.stringify(this.settingList))
					
					// 显示保存成功标记
					changedSettings.forEach(item => {
						this.$set(this.savedItems, item.settingsName, true)
					})
					
					// 3秒后清除保存标记
					setTimeout(() => {
						changedSettings.forEach(item => {
							if (this.savedItems[item.settingsName]) {
								this.$delete(this.savedItems, item.settingsName)
							}
						})
					}, 3000)
					
					this.$Notice.success({
						title: '保存成功',
						duration: 3,
						desc: `成功保存 ${changedSettings.length} 项设置`
					})
					
				} catch (error) {
					this.$Notice.error({
						title: '保存失败',
						duration: 3,
						desc: error.message || '请稍后重试'
					})
				} finally {
					this.saving = false
				}
			},
			
			// 初始化设置列表
			initSettings() {
				request.get('/api/SystemSettings/GetSystemSettingsList', {
					params: { id: 0 }
				}).then(response => {
					if (response.state == 0) {
						this.$Notice.error({
							title: '错误提示',
							duration: 3,
							desc: response.errorMsg || '获取设置失败'
						});
						return;
					}
					this.settingList = response.data || [];
					// 深拷贝备份原始数据
					this.originalSettingList = JSON.parse(JSON.stringify(this.settingList));
					this.savedItems = {};
				}).catch(error => {
					this.$Notice.error({
						title: '网络错误',
						duration: 3,
						desc: '无法连接服务器'
					});
				})
			}
		},
		created: function() {
			this.initSettings();
		}
	}
</script>

<style scoped>
	.system-settings-container {
		min-height: 100%;
		background: linear-gradient(135deg, #f5f7fa 0%, #f0f2f5 100%);
		padding: 15px;
	}

	/* 页面头部 */
	.settings-header {
		margin-bottom: 15px;
		padding: 0 8px;
	}

	.header-title {
		display: flex;
		align-items: center;
		gap: 12px;
		margin-bottom: 8px;
	}

	.title-icon {
		font-size: 32px;
	}

	.header-title h2 {
		font-size: 24px;
		font-weight: 600;
		color: #1a1a2e;
		margin: 0;
		letter-spacing: -0.3px;
	}

	.header-desc {
		font-size: 14px;
		color: #6c757d;
		margin: 0 0 0 44px;
	}

	/* 设置内容区域 */
	.settings-content {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	/* 设置卡片 */
	.setting-card {
		background: #ffffff;
		border-radius: 16px;
		padding: 20px 24px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		transition: all 0.25s ease;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
		border: 1px solid rgba(0, 0, 0, 0.05);
	}

	.setting-card:hover {
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
		border-color: rgba(64, 158, 255, 0.2);
		transform: translateY(-1px);
	}

	/* 卡片左侧 */
	.card-left {
		display: flex;
		align-items: center;
		gap: 16px;
		flex: 1;
	}

	.setting-icon {
		width: 48px;
		height: 48px;
		background: linear-gradient(135deg, #f0f7ff 0%, #e8f0fe 100%);
		border-radius: 14px;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 24px;
		transition: all 0.2s;
	}

	.setting-card:hover .setting-icon {
		background: linear-gradient(135deg, #e8f0fe 0%, #dce8fa 100%);
		transform: scale(1.02);
	}

	.setting-info {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.setting-label {
		font-size: 15px;
		font-weight: 600;
		color: #1e293b;
		letter-spacing: -0.2px;
	}

	.setting-desc {
		font-size: 12px;
		color: #94a3b8;
		line-height: 1.4;
	}

	/* 卡片右侧 */
	.card-right {
		display: flex;
		align-items: center;
		gap: 16px;
		min-width: 280px;
		justify-content: flex-end;
	}

	/* 自定义输入框 */
	.custom-input {
		width: 280px;
	}

	.custom-input >>> .ivu-input {
		border-radius: 10px;
		border-color: #e2e8f0;
		transition: all 0.2s;
		font-size: 14px;
	}

	.custom-input >>> .ivu-input:focus {
		border-color: #409eff;
		box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1);
	}

	.custom-input.input-changed >>> .ivu-input {
		border-color: #f5a623;
		background-color: #fffbf5;
	}

	/* 自定义下拉框 */
	.custom-select {
		width: 280px;
	}

	.custom-select >>> .ivu-select-selection {
		border-radius: 10px;
		border-color: #e2e8f0;
		transition: all 0.2s;
	}

	.custom-select >>> .ivu-select-selection:hover {
		border-color: #409eff;
	}

	.custom-select.select-changed >>> .ivu-select-selection {
		border-color: #f5a623;
		background-color: #fffbf5;
	}

	/* 已保存标记 */
	.saved-badge {
		background: #e8f5e9;
		border-radius: 20px;
		padding: 4px 12px;
		font-size: 12px;
		color: #2e7d32;
		font-weight: 500;
		display: flex;
		align-items: center;
		gap: 4px;
		animation: fadeInOut 0.3s ease;
	}

	.saved-badge span::before {
		content: "✓";
		margin-right: 4px;
		font-weight: bold;
	}

	@keyframes fadeInOut {
		0% {
			opacity: 0;
			transform: scale(0.9);
		}
		100% {
			opacity: 1;
			transform: scale(1);
		}
	}

	/* 空状态 */
	.empty-state {
		text-align: center;
		padding: 80px 20px;
		background: #ffffff;
		border-radius: 20px;
	}

	.empty-icon {
		font-size: 64px;
		opacity: 0.5;
		display: block;
		margin-bottom: 16px;
	}

	.empty-state p {
		color: #94a3b8;
		font-size: 14px;
	}

	/* 底部操作栏 */
	.settings-footer {
		position: sticky;
		bottom: 24px;
		margin-top: 28px;
		background: #ffffff;
		border-radius: 60px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 12px 24px;
		box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.02);
		border: 1px solid rgba(0, 0, 0, 0.05);
		backdrop-filter: blur(10px);
		background: rgba(255, 255, 255, 0.98);
	}

	.footer-info {
		display: flex;
		align-items: center;
	}

	.info-badge {
		background: #fff7e6;
		border-radius: 30px;
		padding: 6px 16px;
		font-size: 13px;
		color: #f5a623;
		font-weight: 500;
	}

	.footer-actions {
		display: flex;
		gap: 12px;
	}

	.btn-reset,
	.btn-save {
		border-radius: 40px;
		padding: 8px 24px;
		font-size: 14px;
		font-weight: 500;
		display: flex;
		align-items: center;
		gap: 8px;
		transition: all 0.2s;
	}

	.btn-reset {
		background: transparent;
		border: 1px solid #d0d5dd;
		color: #475569;
	}

	.btn-reset:hover {
		border-color: #94a3b8;
		background: #f8fafc;
		transform: translateY(-1px);
	}

	.btn-save {
		background: rgb(255, 106, 127);
		border: none;
		box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
	}

	.btn-save:hover {
		transform: translateY(-1px);
		box-shadow: 0 4px 12px rgba(64, 158, 255, 0.4);
	}

	/* 响应式适配 */
	@media (max-width: 768px) {
		.system-settings-container {
			padding: 16px;
		}
		
		.setting-card {
			flex-direction: column;
			align-items: flex-start;
			gap: 16px;
		}
		
		.card-right {
			width: 100%;
			min-width: auto;
		}
		
		.custom-input,
		.custom-select {
			width: 100%;
		}
		
		.settings-footer {
			flex-direction: column;
			gap: 12px;
			border-radius: 20px;
			padding: 16px;
		}
		
		.footer-actions {
			width: 100%;
		}
		
		.btn-reset,
		.btn-save {
			flex: 1;
			justify-content: center;
		}
	}
</style>